import { ReactNode } from "react";
import {
  ApartmentOutlined,
  ApiOutlined,
  BankOutlined,
  BranchesOutlined,
  ClusterOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  DeliveredProcedureOutlined,
  ForkOutlined,
  GiftOutlined,
  MergeOutlined,
  PartitionOutlined,
  ProfileOutlined,
  ReconciliationOutlined,
  TeamOutlined,
  UnlockOutlined,
  UnorderedListOutlined,
  ControlOutlined,
  ClockCircleOutlined,
  FundOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { DICTIONARY_LIST_PATH } from "@/modules/common/pages/dictionaries/modules-dictionaries";
import { AllTokens, EnabledTokens } from "gql/graphql";
import { ADMIN_PANEL_ROLES_PATH } from "@/modules/roles/pages/roles";
import { OAUTH_SETTINGS_PATH } from "@/modules/common/pages/oauth-settings";
import { HOLIDAY_SETTINGS_PATH } from "@/modules/common/pages/holiday-settings";
import { HR, RECRUIT } from "@/shared/modules/constants/modules-id";
import { HH_INTEGRATION_PATH } from "@/modules/recruiting/pages/hh-integration";
import { BPM_ROLES_MAIN_PATH } from "@/modules/admin-panel/pages/bpm-roles/main";
import { BPM_PROCESS_EDITOR_PATH } from "@/modules/admin-panel/bpm-process/page/bpm-process-editor";
import { DYNAMIC_PROCESSES_LIST_PATH } from "@/modules/admin-panel/dynamic-processes/pages/dynamic-processes-list";
import { DYNAMIC_ATTRIBUTE_EDITOR_PATH } from "@/modules/dynamic-attributes/pages";
import { DYNAMIC_ENUM_EDITOR } from "@/modules/dynamic-attributes/pages/enum-editor";
import { ABSENCE_TYPE_POLICY_PATH } from "@/modules/absence-politics/pages/absence-type-list";
import { NOTIFICATION_TEMPLATE_LIST_PATH } from "@/modules/admin-panel/notification-templates/pages/notification-template-list";
import { HISTORICAL_DATA_IMPORT_LIST_PATH } from "@/modules/historical-data-import/pages/historical-data-import";
import { DISK_SPACE_PATH } from "@/modules/file-storage/pages/disc-space";
import { COMPANY_SETTINGS_PATH } from "@/modules/common/pages/company-settings";
import { RAG_DOCUMENTS_PATH } from "@/modules/ai/pages/rag-documents";
import { AI_TOKEN_USAGE_PATH } from "@/modules/ai/pages/ai-token-usage";
import { MODULE_SETTINGS_PATH } from "@/modules/global-wizard/pages/module-settings";
import { TRIGGER_ACTION_LIST_PATH } from "@/modules/automation/pages/trigger-action-list";
import { OVERDUE_TASKS_NOTIFICATION_SETTINGS_PATH } from "@/modules/tasks/pages/overdue-tasks-notification-settings";
import { SYSTEM_CHECK_PATH } from "@/modules/system-check/pages/system-check";

type Customization = {
  customIconColor?: string;
  customIconBackgroundColor?: string;
};

export type SettingsMenuItem = {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
  to: string;
  requiredModule?: string;
  requiredAtLeastOnePermission?: string[];
};

export type SettingsMenuBlock = Customization & {
  id: string;
  title: string;
  items: SettingsMenuItem[];
};

export const SETTINGS_MENU_DATA: SettingsMenuBlock[] = [
  {
    id: "general",
    title: "GENERAL_BLOCK",
    items: [
      {
        id: "dictionary-management",
        title: "DICTIONARIES_TITLE",
        description: "DICTIONARIES_DESCRIPTION",
        icon: <UnorderedListOutlined />,
        to: DICTIONARY_LIST_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminCompanyDictionaries],
      },
      {
        id: "role-management",
        title: "ROLES_TITLE",
        description: "ROLES_DESCRIPTION",
        icon: <TeamOutlined />,
        to: ADMIN_PANEL_ROLES_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminAccessRoles],
      },
      {
        id: "authentication",
        title: "AUTHENTICATION_TITLE",
        description: "AUTHENTICATION_DESCRIPTION",
        icon: <UnlockOutlined />,
        to: OAUTH_SETTINGS_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminAccessAuthentication],
      },
      {
        id: "hh-integration",
        title: "HH_INTEGRATION_TITLE",
        description: "HH_INTEGRATION_DESCRIPTION",
        icon: <ApiOutlined />,
        to: HH_INTEGRATION_PATH,
        requiredModule: RECRUIT,
        requiredAtLeastOnePermission: [EnabledTokens.RecruitingHhIntegration],
      },
      {
        id: "administrating",
        title: "HOLIDAYS_TITLE",
        description: "HOLIDAYS_DESCRIPTION",
        icon: <GiftOutlined />,
        to: HOLIDAY_SETTINGS_PATH,
        requiredModule: HR,
        requiredAtLeastOnePermission: [EnabledTokens.AdminCompanyHolidays],
      },
      {
        id: "absence-policy",
        title: "ABSENCE_POLICY_TITLE",
        description: "ABSENCE_POLICY_DESCRIPTION",
        icon: <ReconciliationOutlined />,
        to: ABSENCE_TYPE_POLICY_PATH,
        requiredModule: HR,
        requiredAtLeastOnePermission: [EnabledTokens.EmployeesAbsencesPolicy],
      },
      {
        id: "company-settings",
        title: "COMPANY_SETTINGS_TITLE",
        description: "COMPANY_SETTINGS_DESCRIPTION",
        icon: <BankOutlined />,
        to: COMPANY_SETTINGS_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminCompanySettings],
      },
    ],
  },
  {
    id: "bpm",
    title: "BPM_BLOCK",
    customIconColor: "#7A3700",
    customIconBackgroundColor: "#FFF8E6",
    items: [
      {
        id: "bpm-roles",
        title: "BPM_ROLES_TITLE",
        description: "BPM_ROLES_DESCRIPTION",
        icon: <PartitionOutlined />,
        to: BPM_ROLES_MAIN_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.TasksRoles],
      },
      {
        id: "bpm-processes",
        title: "BPM_PROCESSES_TITLE",
        description: "BPM_PROCESSES_DESCRIPTION",
        icon: <ForkOutlined />,
        to: BPM_PROCESS_EDITOR_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.TasksProcesses],
      },
      {
        id: "dynamic-bpm-processes",
        title: "DYNAMIC_BPM_PROCESSES_TITLE",
        description: "DYNAMIC_BPM_PROCESSES_DESCRIPTION",
        icon: <MergeOutlined />,
        to: DYNAMIC_PROCESSES_LIST_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.TasksDynamicProcesses],
      },
      {
        id: "dynamic-attributes",
        title: "DYNAMIC_ATTRIBUTES_TITLE",
        description: "DYNAMIC_ATTRIBUTES_DESCRIPTION",
        icon: <BranchesOutlined />,
        to: DYNAMIC_ATTRIBUTE_EDITOR_PATH,
        requiredAtLeastOnePermission: [
          EnabledTokens.AdminCompanyDynamicAttributes,
        ],
      },
      {
        id: "dynamic-enums",
        title: "DYNAMIC_ENUMS_TITLE",
        description: "DYNAMIC_ENUMS_DESCRIPTION",
        icon: <ProfileOutlined />,
        to: DYNAMIC_ENUM_EDITOR,
        requiredAtLeastOnePermission: [
          EnabledTokens.AdminCompanyDynamicAttributes,
        ],
      },
    ],
  },
  {
    id: "administrating",
    title: "ADMINISTRATING_BLOCK",
    customIconColor: "#004207",
    customIconBackgroundColor: "#E2F5DF",
    items: [
      {
        id: "notification-templates",
        title: "NOTIFICATION_TEMPLATES_TITLE",
        description: "NOTIFICATION_TEMPLATES_DESCRIPTION",
        icon: <ContainerOutlined />,
        to: NOTIFICATION_TEMPLATE_LIST_PATH,
        requiredAtLeastOnePermission: [
          EnabledTokens.AdminCompanyNotificationTemplates,
        ],
      },
      {
        id: "historical-data",
        title: "HISTORICAL_DATA_TITLE",
        description: "HISTORICAL_DATA_DESCRIPTION",
        icon: <DeliveredProcedureOutlined />,
        to: HISTORICAL_DATA_IMPORT_LIST_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminCompanyImport],
      },
      {
        id: "disk-space",
        title: "DISK_SPACE_TITLE",
        description: "DISK_SPACE_DESCRIPTION",
        icon: <ClusterOutlined />,
        to: DISK_SPACE_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminStorageInfo],
      },
      {
        id: "module-settings",
        title: "MODULE_SETTINGS_TITLE",
        description: "MODULE_SETTINGS_DESCRIPTION",
        icon: <ApartmentOutlined />,
        to: MODULE_SETTINGS_PATH,
        requiredModule: HR,
      },
      {
        id: "overdue-tasks-notification-settings",
        title: "OVERDUE_TASKS_NOTIFICATION_SETTINGS_TITLE",
        description: "OVERDUE_TASKS_NOTIFICATION_SETTINGS_DESCRIPTION",
        icon: <ClockCircleOutlined />,
        to: OVERDUE_TASKS_NOTIFICATION_SETTINGS_PATH,
        requiredAtLeastOnePermission: [AllTokens.TasksOverdueNotification],
      },
      {
        id: "system-check",
        title: "SYSTEM_CHECK_TITLE",
        description: "SYSTEM_CHECK_DESCRIPTION",
        icon: <SafetyCertificateOutlined />,
        to: SYSTEM_CHECK_PATH,
        requiredAtLeastOnePermission: [EnabledTokens.AdminSystemCheck],
      },
    ],
  },
  {
    id: "automation",
    title: "AUTOMATION_BLOCK",
    customIconColor: "#0047AB",
    customIconBackgroundColor: "#E7F0FF",
    items: [
      {
        id: "automation-rules",
        title: "AUTOMATION_TITLE",
        description: "AUTOMATION_DESCRIPTION",
        icon: <ControlOutlined />,
        to: TRIGGER_ACTION_LIST_PATH,
      },
    ],
  },
  {
    id: "ai",
    title: "AI_BLOCK",
    items: [
      {
        id: "rag-documents",
        title: "RAG_DOCUMENT_TITLE",
        description: "RAG_DOCUMENT_DESCRIPTION",
        icon: <DatabaseOutlined />,
        to: RAG_DOCUMENTS_PATH,
      },
      {
        id: "ai-token-usage",
        title: "AI_TOKEN_USAGE_TITLE",
        description: "AI_TOKEN_USAGE_DESCRIPTION",
        icon: <FundOutlined />,
        to: AI_TOKEN_USAGE_PATH,
        requiredAtLeastOnePermission: [
          EnabledTokens.AdminAiTokenUsageStatistics,
        ],
      },
    ],
  },
];
